import { BaseProps } from "../../BaseProps";
import { DataSource } from "../DataSource";
import { Stack } from "../../layout";
import { HorizontalLine } from "../HorizontalLine";
import { ReactNode, useId } from "react";
import { Paged } from "../Paged";
import { Paragraph } from "../../typography";
import { Actions } from "./ListActions";
import { DataListRowContext } from "./DataListRowContext";
import { DataId } from "../DataId";
import { LoadingGroup } from "../LoadingGroup";
import { LoadingState } from "./LoadingState";

/** The type of data source accepted by the `DataList` component. */
export interface DataListDataSource<ListItem extends object> extends DataSource<ListItem>, Paged {}

/** The `DataList` component properties. */
export interface DataListProps<ListItem extends object> extends BaseProps {
  /** The data source. */
  readonly dataSource: DataListDataSource<ListItem>;

  /** Render function for the main content of each row. */
  readonly labelProvider: (row: ListItem) => ReactNode;

  /** Render function for the actions content of each row. */
  readonly renderActions?: (id: DataId) => ReactNode;

  /** The loading message */
  readonly loadingMessage?: ReactNode;

  /** Heading or title for the DataList. */
  readonly title?: ReactNode;

  /** Message displayed when there are no rows. */
  readonly emptyMessage?: ReactNode;

  /** The DataList footer */
  readonly footer?: ReactNode;

  /** Indicates whether bottom row separator is hidden. If not specified defaults to false. */
  readonly showBottomSeparator?: boolean;
}

/** A DataList component. */
export const DataList = <ListItem extends object>(props: DataListProps<ListItem>) => {
  const { dataSource, title, emptyMessage, footer, showBottomSeparator = false, loadingMessage = "Loading", testId, labelProvider, renderActions } = props;

  const rows = dataSource.rows;
  const isLoading = dataSource.busy;

  return (
    <LoadingGroup loading={isLoading}>
      <LoadingState loadingMessage={loadingMessage} testId={testId}>
        <Stack role="list" spacing="medium" testId={testId} aria-busy={isLoading}>
          {title}
          {(!rows || rows.length === 0) ? (
            <div role="listitem">
              {emptyMessage ?? <Paragraph>None</Paragraph>}
            </div>
          ) : (
            <>
              {rows.map((element, index) => {
                const elementID = useId();
                const isLast = index === rows.length - 1;
                const { id, data } = element;
                return (
                  <Stack
                    role="listitem"
                    key={elementID}
                    aria-posinset={index + 1}
                    aria-setsize={rows.length}
                  >
                    <DataListRowContext.Provider value={{ row: element, id }} >
                      <Stack
                        spacing="mini"
                        testId={testId ? `${testId}-row-${index}` : `datalist-row-${index}`}
                      >
                        {labelProvider(data)}
                        {renderActions && renderActions(id)}
                      </Stack>
                      {(!isLast || showBottomSeparator) && <HorizontalLine role="separator" aria-hidden="true" />}
                    </DataListRowContext.Provider>
                  </Stack>
                );
              })}
            </>
          )}
          {footer && <div role="complementary" aria-label="List Footer">{footer}</div>}
        </Stack>
      </LoadingState>
    </LoadingGroup>
  );
};

/** Section which displays a series of actions. */
DataList.Actions = Actions;
